import qs from "qs";
import { FilterDef, HTTPGenericResponse } from "../../constants/FinanceModels";
import { getFilterValue } from "../../utils/FilterUtils";
import { useApi } from "../Api";
import {
    ID_FILTER_CAR_NUMBER,
    ID_FILTER_CARD_CUSTOMER,
    ID_FILTER_CARD_NUMBER,
    ID_FILTER_CATEGORY,
    ID_FILTER_DEALERS,
    ID_FILTER_IMPORT_DATE_FROM,
    ID_FILTER_IMPORT_DATE_TO,
    ID_FILTER_INVOICE_DATE_FROM,
    ID_FILTER_INVOICE_DATE_TO,
    ID_FILTER_INVOICE_NUMBER,
    ID_FILTER_LEGAL_ENTITIES,
    ID_FILTER_RA,
    ID_FILTER_REFUELLING_DATE_FROM,
    ID_FILTER_REFUELLING_DATE_TO,
    ID_FILTER_REPORT_FILE_NAME,
    ID_FILTER_REPORT_IMPORTED_BY, ID_FILTER_REPORT_STATUS,
    ID_FILTER_REPORT_TOTAL_REFUELLING_FROM,
    ID_FILTER_REPORT_TOTAL_REFUELLING_TO,
    ID_FILTER_SEARCH,
    ID_FILTER_STATUS,
    ID_FILTER_SUPPLIER
} from "../../pages/cardfuel/constants/FuelFilters";
import dayjs from "dayjs";
import {useContext} from "react";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";

export const useFuelCardsRepository = () => {
    const {GET, POST, PUT, DELETE} = useApi();

    const PATH = "/finance/fuelcards";
    const {  globalData: { user } } = useContext(GlobalContext);
    const allLegalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all").map((x: any) => x.name);

    function getFuelCardsReports(offset: number, limit: number, sort: string | null, order: string | null, filterDefs: FilterDef[] = []): Promise<HTTPGenericResponse> {
        let search = getFilterValue(ID_FILTER_SEARCH, filterDefs);
        let supplier = getFilterValue(ID_FILTER_SUPPLIER, filterDefs);
        let status = getFilterValue(ID_FILTER_REPORT_STATUS, filterDefs);
        let importDateFrom: Date = getFilterValue(ID_FILTER_IMPORT_DATE_FROM, filterDefs);
        let importDateTo: Date = getFilterValue(ID_FILTER_IMPORT_DATE_TO, filterDefs);
        let fileName = getFilterValue(ID_FILTER_REPORT_FILE_NAME, filterDefs);
        let importedBy = getFilterValue(ID_FILTER_REPORT_IMPORTED_BY, filterDefs);
        let totalRefuellingFrom = getFilterValue(ID_FILTER_REPORT_TOTAL_REFUELLING_FROM, filterDefs);
        let totalRefuellingTo = getFilterValue(ID_FILTER_REPORT_TOTAL_REFUELLING_TO, filterDefs);
        let legalEntities: string[] = getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) || allLegalEntities;
        let dealers: string[] = getFilterValue(ID_FILTER_DEALERS, filterDefs);

        let params = {
            offset: offset,
            limit: limit,
            sort: sort,
            order: order,
            search: search,
            supplier: supplier,
            importDateFrom: importDateFrom ? dayjs(importDateFrom).toISOString() : null,
            importDateTo: importDateTo ? dayjs(importDateTo).toISOString() : null,
            fileName: fileName,
            importedBy: importedBy,
            totalRefuellingFrom: totalRefuellingFrom,
            totalRefuellingTo: totalRefuellingTo,
            status: status,
            legalEntities,
            dealers
        }

        return GET(`${PATH}/import`,
            {
                params: params, paramsSerializer: (params) => qs.stringify(params,
                    {arrayFormat: 'repeat', skipNulls: true})
            });
    }

    function getFuelCard(offset: number, limit: number, sort: string | null, order: string | null, filterDefs: FilterDef[] = [], reportId: string | undefined, legalEntity: string | null, dealer: string | null): Promise<HTTPGenericResponse> {
        let search = getFilterValue(ID_FILTER_SEARCH, filterDefs);
        let ra = getFilterValue(ID_FILTER_RA, filterDefs);
        let carNumber = getFilterValue(ID_FILTER_CAR_NUMBER, filterDefs);
        let cardCustomer = getFilterValue(ID_FILTER_CARD_CUSTOMER, filterDefs);
        let cardNumber = getFilterValue(ID_FILTER_CARD_NUMBER, filterDefs);
        let category = getFilterValue(ID_FILTER_CATEGORY, filterDefs);
        let invoiceNumber = getFilterValue(ID_FILTER_INVOICE_NUMBER, filterDefs);
        let refuellingDateFrom: Date = getFilterValue(ID_FILTER_REFUELLING_DATE_FROM, filterDefs);
        let refuellingDateTo: Date = getFilterValue(ID_FILTER_REFUELLING_DATE_TO, filterDefs);
        let invoiceDateFrom: Date = getFilterValue(ID_FILTER_INVOICE_DATE_FROM, filterDefs);
        let invoiceDateTo: Date = getFilterValue(ID_FILTER_INVOICE_DATE_TO, filterDefs);
        let status = getFilterValue(ID_FILTER_STATUS, filterDefs);
        let legalEntities: string[] = getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs);
        let dealers: string[] = getFilterValue(ID_FILTER_DEALERS, filterDefs);

        let params = {
            offset: offset,
            limit: limit,
            sort: sort,
            order: order,
            search: search,
            raNumber: ra,
            vehicleSapNumber: carNumber,
            cardNumber: cardNumber,
            status: status,
            cardCustomer: cardCustomer,
            category: category,
            invoiceNumber: invoiceNumber,
            transactionDatetimeFrom: refuellingDateFrom ? dayjs(refuellingDateFrom).toISOString() : null,
            transactionDatetimeTo: refuellingDateTo ? dayjs(refuellingDateTo).toISOString() : null,
            invoiceDateFrom: invoiceDateFrom ? dayjs(invoiceDateFrom).toISOString() : null,
            invoiceDateTo: invoiceDateTo ? dayjs(invoiceDateTo).toISOString() : null,
            reportId: reportId,
            legalEntities: [legalEntity],
            dealers: [dealer]
        }

        return GET(`${PATH}`,
            {
                params: params, paramsSerializer: (params) => qs.stringify(params,
                    {arrayFormat: 'repeat', skipNulls: true})
            });
    }

    function getReportById(id: string, config?: any): Promise<HTTPGenericResponse> {
        return GET(`${PATH}/import/single?id=${id}`, config);
    }

    function fuelCardDetail(id: string | undefined): Promise<HTTPGenericResponse> {
        return GET(`${PATH}/single?id=` + id);
    }

    function updateFuel(payload: any): Promise<HTTPGenericResponse> {
        return PUT(`${PATH}`, payload, {}, true);
    }

    function importFile(file: File, supplier: string, legalEntity: string, dealer: string): Promise<HTTPGenericResponse> {
        let formData = new FormData();
        formData.append("refuelFile", file);
        formData.append("supplier", supplier);
        formData.append("legalEntity", legalEntity);
        formData.append("dealer", dealer);
        
        return POST(`${PATH}/refuel/upload`, formData,
            {headers: {"Content-Type": "multipart/form-data"}}, true)
    }

    function deleteRefuel(id: string): Promise<HTTPGenericResponse> {
        return DELETE(`${PATH}/?id=` + id, {}, true);
    }
    function deleteReport(id: string): Promise<HTTPGenericResponse> {
        return DELETE(`${PATH}/import?id=` + id, {}, true);
    }

    function deleteRefuelsList(payload: any): Promise<HTTPGenericResponse> {
        return DELETE(`${PATH}/ids`, { data: payload }, true);
    }

    return {
        getFuelCardsReports,
        getReportById,
        getFuelCard,
        importFile,
        updateFuel,
        deleteRefuel,
        deleteReport,
        deleteRefuelsList
    };
}